import {
  Box,
  VStack,
  Text,
  Button,
  Image,
  SimpleGrid,
  HStack,
  IconButton,
  Spinner,
  Card,
  CardBody,
} from '@chakra-ui/react'
import { useRef, useState } from 'react'
import { MdCloudUpload, MdClose, MdImage } from 'react-icons/md'

interface ImageUploadZoneProps {
  onImagesUploaded: (imageUrls: string[]) => void
  uploadedImages: string[]
  analyzing: boolean
}

export default function ImageUploadZone({
  onImagesUploaded,
  uploadedImages,
  analyzing,
}: ImageUploadZoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return

    const imageUrls: string[] = []
    Array.from(files).forEach((file) => {
      if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file)
        imageUrls.push(url)
      }
    })

    if (imageUrls.length > 0) {
      onImagesUploaded([...uploadedImages, ...imageUrls])
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    handleFileSelect(e.dataTransfer.files)
  }

  const handleRemoveImage = (index: number) => {
    const newImages = uploadedImages.filter((_, i) => i !== index)
    onImagesUploaded(newImages)
  }

  return (
    <VStack spacing={4} align="stretch">
      {/* Upload Zone */}
      {uploadedImages.length === 0 ? (
        <Card
          borderWidth={2}
          borderStyle="dashed"
          borderColor={isDragging ? 'purple.500' : 'gray.300'}
          bg={isDragging ? 'purple.50' : 'gray.50'}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          cursor="pointer"
          onClick={() => fileInputRef.current?.click()}
          transition="all 0.2s"
          _hover={{ borderColor: 'purple.400', bg: 'purple.50' }}
        >
          <CardBody>
            <VStack spacing={4} py={8}>
              <MdCloudUpload style={{ fontSize: '64px', color: 'var(--chakra-colors-purple-500)' }} />
              <VStack spacing={2}>
                <Text fontSize="lg" fontWeight="semibold">
                  Drop images here or click to upload
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Supports: JPG, PNG, DICOM files
                </Text>
              </VStack>
              <Button colorScheme="purple" size="lg">
                Select Images
              </Button>
            </VStack>
          </CardBody>
        </Card>
      ) : (
        <VStack spacing={4} align="stretch">
          {/* Image Grid */}
          <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4}>
            {uploadedImages.map((url, index) => (
              <Box key={index} position="relative">
                <Image
                  src={url}
                  alt={`Upload ${index + 1}`}
                  borderRadius="md"
                  objectFit="cover"
                  w="100%"
                  h="200px"
                  border="2px solid"
                  borderColor="gray.200"
                />
                <IconButton
                  aria-label="Remove image"
                  icon={<MdClose />}
                  size="sm"
                  colorScheme="red"
                  position="absolute"
                  top={2}
                  right={2}
                  onClick={() => handleRemoveImage(index)}
                  isDisabled={analyzing}
                />
              </Box>
            ))}
          </SimpleGrid>

          {/* Add More Button */}
          <Button
            leftIcon={<MdImage />}
            onClick={() => fileInputRef.current?.click()}
            variant="outline"
            colorScheme="purple"
            isDisabled={analyzing}
          >
            Add More Images
          </Button>

          {/* Analysis Status */}
          {analyzing && (
            <HStack
              p={4}
              bg="purple.50"
              borderRadius="md"
              justify="center"
              spacing={3}
            >
              <Spinner color="purple.500" />
              <Text color="purple.700" fontWeight="semibold">
                Analyzing images with AI...
              </Text>
            </HStack>
          )}
        </VStack>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => handleFileSelect(e.target.files)}
      />
    </VStack>
  )
}
